import { dataSystemsAPI } from "./dataSystemsAPI";

/**
 * Creates RTK 'useQuery' hooks for the endpoints defined in the 'endpoints'
 * object.
 */
export const userEndpoints = dataSystemsAPI.injectEndpoints({
    reducerPath: "userEndpoints",
    overrideExisting: false,
    endpoints: (builder) => ({
        getUser: builder.query({
            query: ({ userId }) => ({ url: `/users/${userId}` }),
            providesTags: ["User"],
        }),
        getUserSavedProperties: builder.query({
            query: ({ userID, filters = {} }) => {
                const queryString = buildQueryString(filters);
                return {
                    url: `/users/${userID}/userproperties${
                        queryString ? `?${queryString}` : ""
                    }`,
                };
            },
            providesTags: ["UserSavedProperties"],
        }),
        updateUser: builder.mutation({
            query: ({ userId, body }) => {
                return {
                    url: `/users/${userId}`,
                    method: "PATCH",
                    body,
                };
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User"],
        }),
        updateUserEmail: builder.mutation({
            query: ({ userId, body }) => {
                return {
                    url: `/users/${userId}/email/change/confirm`,
                    method: "POST",
                    body,
                };
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User"],
        }),
        verifyUserEmail: builder.mutation({
            query: ({ userId, email, reset_code }) => {
                return {
                    url: `/users/${userId}/email/verify/confirm`,
                    method: "POST",
                    body: { email, reset_code },
                };
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User"],
        }),
        resendVerifyEmailCode: builder.mutation({
            query: ({ userId }) => {
                return {
                    url: `/users/${userId}/email/verify`,
                    method: "POST",
                    body: {},
                };
            },
            extraOptions: { maxRetries: 0 },
        }),
        deleteUser: builder.mutation({
            query: ({ userId }) => {
                return {
                    url: `/users/${userId}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["User"],
        }),
        saveUserProperty: builder.mutation({
            query: ({ userId, listingNumber }) => ({
                url: `/users/${userId}/userproperties/${listingNumber}`,
                method: "POST",
            }),
            async onQueryStarted(
                { userId, listingNumber },
                { dispatch, queryFulfilled }
            ) {
                dispatch(addPropertyToSavedProperties(userId, listingNumber));
                try {
                    const { data } = await queryFulfilled;
                    if (!data.includes(listingNumber)) {
                        throw new Error("Missing listing number from response");
                    }
                } catch {
                    userEndpoints.util.invalidateTags(["User"]);
                }
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User", "UserSavedProperties"],
        }),
        updateUserProperty: builder.mutation({
            query: ({ userId, listingId, body }) => ({
                url: `/users/${userId}/userproperties/${listingId}`,
                method: "PATCH",
                body,
            }),
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User", "UserSavedProperties"],
        }),
        deleteUserProperty: builder.mutation({
            query: ({ userId, listingNumber }) => ({
                url: `/users/${userId}/userproperties/${listingNumber}`,
                method: "DELETE",
            }),
            async onQueryStarted(
                { userId, listingNumber },
                { dispatch, queryFulfilled }
            ) {
                dispatch(
                    removePropertyFromSavedProperties(userId, listingNumber)
                );
                try {
                    const { data } = await queryFulfilled;
                    if (data.includes(listingNumber)) {
                        throw new Error(
                            "Deleted, but listing number in response"
                        );
                    }
                } catch {
                    userEndpoints.util.invalidateTags(["User"]);
                }
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User", "UserSavedProperties"],
        }),
        updateAllUserPropertyAlerts: builder.mutation({
            query: ({ userId, alertType }) => {
                return {
                    url: `/users/${userId}/userproperties/alerts/${alertType}`,
                    method: "PUT",
                };
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User"],
        }),
        updateAllUserSearchAlerts: builder.mutation({
            query: ({ userId, alertType }) => {
                return {
                    url: `/users/${userId}/usersearch/alerts/${alertType}`,
                    method: "PUT",
                };
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User"],
        }),
        saveUserSearch: builder.mutation({
            query: ({ userId, body }) => {
                return {
                    url: `/users/${userId}/usersearch`,
                    method: "POST",
                    body,
                };
            },
            invalidatesTags: ["User"],
        }),
        updateUserSearch: builder.mutation({
            query: ({ userId, id, body }) => {
                return {
                    url: `/users/${userId}/usersearch/${id}`,
                    method: "PATCH",
                    body,
                };
            },
            extraOptions: { maxRetries: 0 },
            invalidatesTags: ["User"],
        }),
        deleteUserSearch: builder.mutation({
            query: ({ userId, id }) => {
                return {
                    url: `/users/${userId}/usersearch/${id}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["User"],
        }),
        bulkDeleteUserProperties: builder.mutation({
            query: ({ userID, removeType }) => ({
                url: `/users/${userID}/userproperties/bulk/${removeType}`,
                method: "DELETE",
            }),
            invalidatesTags: ["User", "UserSavedProperties"],
        }),
    }),
});

// Helper function to build the query string based on filters for saved properties
const buildQueryString = (filters) => {
    const keyMapping = {
        page: "page",
        nicheItem: "nicheItem",
        priceMin: "priceMin",
        priceMax: "priceMax",
        waterfront: "waterfront",
        status: "status",
        bedrooms: "bedrooms",
        bathrooms: "bathrooms",
        property_type: "propertyType",
    };

    let queryString = "";

    Object.entries(filters).forEach(([frontendKey, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            const apiKey = keyMapping[frontendKey] || frontendKey;

            // Handle sort_order transformation
            if (frontendKey === "sort_order" && typeof value === "string") {
                const [item, order] = value.split("_");
                const sortInfo = `{"item":"${item}","order":"${order.toUpperCase()}"}`;
                queryString += `&sortInfo=${sortInfo}`;
            } else if (Array.isArray(value)) {
                // For array values like nicheItem
                if (value.length === 1) {
                    // If only one item, add with quotes
                    queryString += `&${apiKey}="${value[0]}"`;
                } else {
                    // If multiple items, add without quotes
                    value.forEach((val) => {
                        queryString += `&${apiKey}=${val}`;
                    });
                }
            } else if (frontendKey === "waterfront") {
                // Transform true/false to 1/0 for waterfront specifically
                queryString += `&${apiKey}=${value ? 1 : 0}`;
            } else if (frontendKey === "price_min") {
                queryString += `&priceMin=${value}`;
            } else if (frontendKey === "price_max") {
                queryString += `&priceMax=${value}`;
            } else {
                // Handle other parameters as-is
                queryString += `&${apiKey}=${value}`;
            }
        }
    });

    return queryString.startsWith("&") ? queryString.slice(1) : queryString;
};

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetUserQuery,
    useGetUserSavedPropertiesQuery,
    useUpdateUserMutation,
    useUpdateUserEmailMutation,
    useLazyGetUserQuery,
    useDeleteUserMutation,
    useSaveUserPropertyMutation,
    useUpdateUserPropertyMutation,
    useDeleteUserPropertyMutation,
    useSaveUserSearchMutation,
    useUpdateUserSearchMutation,
    useUpdateAllUserPropertyAlertsMutation,
    useUpdateAllUserSearchAlertsMutation,
    useVerifyUserEmailMutation,
    useResendVerifyEmailCodeMutation,
    useDeleteUserSearchMutation,
    useBulkDeleteUserPropertiesMutation,
} = userEndpoints;

/**
 * Removes a property from the user's saved properties in the Redux store.
 *
 * @param {string} userId - The ID of the user whose saved properties are being updated.
 * @param {string} listingNumber - The listing number of the property to be removed.
 * @returns {Function} - A function that updates the query data for the 'getUser' endpoint.
 */
const removePropertyFromSavedProperties = (userId, listingNumber) => {
    return userEndpoints.util.updateQueryData(
        "getUser",
        { userId },
        (draft) => {
            draft.userInfo.dIAccountInfo.savedProperties.userProperties =
                draft?.userInfo?.dIAccountInfo?.savedProperties?.userProperties.filter(
                    (item) => item !== listingNumber
                );
        }
    );
};

/**
 * Updates the user data in the Redux store to add a property to the user's saved properties.
 *
 * @param {string} userId - The ID of the user whose saved properties are being updated.
 * @param {string} listingNumber - The listing number of the property to be added.
 */
const addPropertyToSavedProperties = (userId, listingNumber) => {
    return userEndpoints.util.updateQueryData(
        "getUser",
        { userId },
        (draft) => {
            if (
                Array.isArray(
                    draft?.userInfo?.dIAccountInfo?.savedProperties
                        ?.userProperties
                )
            ) {
                draft?.userInfo?.dIAccountInfo?.savedProperties?.userProperties.push(
                    listingNumber
                );
            }
        }
    );
};
